import { AppLink } from "@/components/ui/app-link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalRelative } from "@/components/ui/local-date";
import { api } from "@/lib/api";
import { createFileRoute, redirect } from "@tanstack/react-router";

/** `/dashboard` — where a client lands: their projects and how the work on them stands. */

type ClientProject = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  openCount: number;
  closedCount: number;
  open: Array<{ name: string; color: string; count: number }>;
  lastActivityAt: string | null;
};

export const Route = createFileRoute("/_app/dashboard")({
  loader: async ({ parentMatchPromise }) => {
    if (!(await parentMatchPromise).loaderData?.isCustomer) throw redirect({ to: "/my" });
    return api.get<{ projects: ClientProject[] }>("/api/me/client-projects");
  },
  component: ClientDashboard,
});

function ClientDashboard() {
  const { projects } = Route.useLoaderData();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your projects and their requests.</p>
      </div>
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          You aren't on any project yet. Your contact at Netlion will add you.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="client-projects">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project: p }: { project: ClientProject }) {
  const workItemsHref = `/projects/${p.slug}/work-items`;
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>
          <AppLink href={workItemsHref} className="hover:underline">
            {p.name}
          </AppLink>
        </CardTitle>
        {p.description && (
          <CardDescription className="line-clamp-2">{p.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="mt-auto space-y-3">
        <p className="text-sm">
          <span className="font-semibold">{p.openCount}</span> open ·{" "}
          <span className="text-muted-foreground">{p.closedCount} done</span>
        </p>
        {p.open.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {p.open.map((s) => (
              <li
                key={s.name}
                className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs"
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.name.replace(/_/g, " ")} {s.count}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {p.lastActivityAt ? (
              <>
                Last change <LocalRelative value={p.lastActivityAt} />
              </>
            ) : (
              "No requests yet"
            )}
          </span>
          <span className="flex gap-3">
            <AppLink href={workItemsHref} className="underline">
              Work items
            </AppLink>
            <AppLink href={`/projects/${p.slug}/wiki`} className="underline">
              Wiki
            </AppLink>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
